import { Controller } from "@hotwired/stimulus"

// Simple dropdown controller for showing/hiding menus.
//
// A toggle marked `aria-haspopup` is treated as a menu button: it carries `aria-expanded`, a
// keyboard opening (a click with `detail` 0) moves the focus to the first item, and closing while
// the focus is inside the menu hands it back to the toggle — else it would fall to the page.
// `keep-in-view` flips an absolutely positioned menu to the toggle's other edge where it would
// leave the viewport (a toolbar that wraps puts its button at either end of a line).
export default class extends Controller {
  static targets = ["menu"]
  static values = { keepInView: Boolean }

  connect() {
    // Ensure menu starts hidden (defensive - handles views that forgot to add 'hidden' class)
    if (this.hasMenuTarget && !this.menuTarget.classList.contains("hidden")) {
      this.menuTarget.classList.add("hidden")
    }

    // Close dropdown when clicking outside
    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener("click", this.boundClickOutside)

    // Close dropdown when pressing ESC
    this.boundKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeydown)

    // Close this dropdown when another dropdown opens
    this.boundCloseOthers = this.closeFromOtherDropdown.bind(this)
    document.addEventListener("dropdown:opened", this.boundCloseOthers)

    // Close dropdown on Turbo navigation
    this.boundCloseOnNavigation = this.close.bind(this)
    document.addEventListener("turbo:before-visit", this.boundCloseOnNavigation)

    // Close dropdown on browser back/forward
    window.addEventListener("popstate", this.boundCloseOnNavigation)

    // Close dropdown after successful form submission (for bulk actions)
    this.boundCloseOnTurboSubmit = this.closeOnTurboSubmit.bind(this)
    document.addEventListener("turbo:submit-end", this.boundCloseOnTurboSubmit)
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
    document.removeEventListener("keydown", this.boundKeydown)
    document.removeEventListener("dropdown:opened", this.boundCloseOthers)
    document.removeEventListener("turbo:before-visit", this.boundCloseOnNavigation)
    window.removeEventListener("popstate", this.boundCloseOnNavigation)
    document.removeEventListener("turbo:submit-end", this.boundCloseOnTurboSubmit)
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  toggle(event) {
    event.stopPropagation()
    if (!this.hasMenuTarget) return
    if (!this.menuTarget.classList.contains("hidden")) return this.close()

    this.menuTarget.classList.remove("hidden")
    if (event.currentTarget?.hasAttribute?.("aria-haspopup")) {
      this.menuButton = event.currentTarget
      this.menuButton.setAttribute("aria-expanded", "true")
      if (event.detail === 0) this.menuTarget.querySelector("button, a[href]")?.focus()
    }
    if (this.keepInViewValue) this.keepMenuInView()

    // Notify other dropdowns to close
    document.dispatchEvent(
      new CustomEvent("dropdown:opened", {
        detail: { element: this.element },
      }),
    )
  }

  closeFromOtherDropdown(event) {
    // Close this dropdown if a different dropdown opened
    if (event.detail.element !== this.element) {
      this.close()
    }
  }

  close() {
    if (!this.hasMenuTarget) return

    const focused = this.menuTarget.contains(document.activeElement)
    this.menuTarget.classList.add("hidden")
    if (!this.menuButton) return

    this.menuButton.setAttribute("aria-expanded", "false")
    if (focused) this.menuButton.focus()
  }

  // Back to the edge the markup sets first, then flipped where that edge pushes it off screen.
  keepMenuInView() {
    const menu = this.menuTarget
    menu.style.left = menu.style.right = ""
    const box = menu.getBoundingClientRect()
    if (box.left < 0) {
      menu.style.left = "0"
      menu.style.right = "auto"
    } else if (box.right > window.innerWidth) {
      menu.style.left = "auto"
      menu.style.right = "0"
    }
  }

  hide(event) {
    // Don't hide if clicking inside the dropdown
    if (this.element.contains(event.target)) {
      return
    }
    this.close()
  }

  clickOutside(event) {
    // Don't close if clicking inside the dropdown
    if (this.element.contains(event.target)) {
      return
    }

    // No special case is needed for the date/time pickers: flatpickr appended its calendar to
    // document.body, which is what broke the containment check above. A popover stays where it
    // is in the DOM, so a panel opened inside this dropdown is still contained by it.

    this.close()
  }

  closeOnTurboSubmit(event) {
    // A settings menu submits once per change and has to survive its own submissions — closing
    // on the first one lets the user change exactly one thing per opening. Such a form marks
    // itself; everything else (a bulk action, where the submit IS the "done") still closes.
    if (event.target.closest?.("[data-dropdown-keep-open]")) {
      return
    }

    // Close dropdown if the form submission originated from within this dropdown
    if (this.element.contains(event.target)) {
      this.close()
    }
  }
}
